/**
 * Utilidades para manejo de imágenes
 */

/**
 * Convierte una imagen local a base64
 * @param {string} imagePath - Ruta de la imagen (ej: '/img/logoColor.png')
 * @returns {Promise<string>} - Promise que resuelve con la imagen en base64
 */
export const convertLocalImageToBase64 = async (imagePath) => {
    try {
        const response = await fetch(imagePath);
        
        if (!response.ok) {
            throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Error al convertir imagen local a base64:', error);
        throw error;
    }
};

/**
 * Convierte una imagen desde URL externa a base64
 * @param {string} imageUrl - URL de la imagen
 * @returns {Promise<string>} - Promise que resuelve con la imagen en base64
 */
export const convertUrlImageToBase64 = async (imageUrl) => {
    try {
        const response = await fetch(imageUrl);
        
        if (!response.ok) {
            throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Error al convertir imagen URL a base64:', error);
        throw error;
    }
};

/**
 * Convierte un archivo de imagen a base64
 * @param {File} file - Archivo de imagen
 * @returns {Promise<string>} - Promise que resuelve con la imagen en base64
 */
export const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Valida si una cadena es un base64 válido
 * @param {string} str - Cadena a validar
 * @returns {boolean} - true si es base64 válido
 */
export const isValidBase64 = (str) => {
    try {
        return str.startsWith('data:image/') && str.includes('base64,');
    } catch (error) {
        return false;
    }
};

/**
 * Obtiene el tipo MIME de una imagen desde base64
 * @param {string} base64String - Cadena base64
 * @returns {string|null} - Tipo MIME o null si no es válido
 */
export const getMimeTypeFromBase64 = (base64String) => {
    try {
        const match = base64String.match(/^data:([^;]+);base64,/);
        return match ? match[1] : null;
    } catch (error) {
        return null;
    }
};

/**
 * Convierte una imagen a base64 con manejo de errores y fallback
 * @param {string} imagePath - Ruta de la imagen
 * @param {string} fallbackImage - Imagen de respaldo en base64
 * @returns {Promise<string>} - Promise que resuelve con la imagen en base64
 */
export const convertImageToBase64WithFallback = async (imagePath, fallbackImage = null) => {
    try {
        return await convertLocalImageToBase64(imagePath);
    } catch (error) {
        console.warn(`No se pudo cargar la imagen ${imagePath}, usando fallback:`, error);
        
        if (fallbackImage) {
            return fallbackImage;
        }
        
        // Crear una imagen de fallback simple (cuadrado gris con texto)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 100;
        canvas.height = 100;
        
        // Fondo gris
        ctx.fillStyle = '#cccccc';
        ctx.fillRect(0, 0, 100, 100);
        
        // Texto
        ctx.fillStyle = '#666666';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('IMG', 50, 50);
        
        return canvas.toDataURL('image/png');
    }
};
